//TODO: global variables on window. Should be reworked and removed from window.
import { RecursivePartial } from '../Types';
import { auto, extend } from 'angular';

import { EventCodes } from './model/event-codes';
import type { ConfigSettings } from './services/mx-config-service';
import type { WorkspaceConfig } from '../WorkspaceLoader/WorkspaceConfig';
import deepMerge from '../Utils/mx-deep-merge';

const portNumber = Number(window.location.port);
const isLocalhostOrIp: boolean = window.location.hostname === 'localhost' || /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(window.location.hostname);
const notForLogPrefix = '##not_for_log##';
const warnLogPrefix = '##warn_log##';
const isDebugMode: boolean = isLocalhostOrIp && portNumber > 3999 && portNumber < 4005;
const defaultRestHost = window.location.origin + '/m42Services';

export const mx = {
	shell: {
		EventCodes,
		config: {} as ConfigSettings,
		Injector: undefined as auto.IInjectorService | undefined,
	},
	SolutionBuilderAgent: {
		Events: EventCodes,
	},
	isDebugMode,
	console: isDebugMode
		? window.console
		: {
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				log: function () {},
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				warn: function () {},
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				error: function () {},
			},
	workspacesConfig: undefined as WorkspaceConfig | undefined,
	forceCatchLogs: false,
	noLogCatchMessage: function (message: string) {
		return `${notForLogPrefix}${message}`;
	},
	warnCatchMessage: function (message: string) {
		return `${warnLogPrefix}${message}`;
	},
	logCatch: function (error: string | { message: string }) {
		const errorMessage = typeof error === 'object' && error?.message ? error.message : error;

		if (typeof errorMessage === 'string' && errorMessage.startsWith(notForLogPrefix)) {
			if (mx.forceCatchLogs) {
				console.error(errorMessage.replace(notForLogPrefix, ''));
			}
			return;
		}

		if (typeof errorMessage === 'string' && errorMessage.startsWith(warnLogPrefix)) {
			console.warn(errorMessage.replace(warnLogPrefix, ''));
			return;
		}
		console.error(errorMessage);
	},
	loadPrismJs: function () {
		// function is used in KbArticleService in backend, in html-literals and kd-editor
		return Promise.all([import(/* webpackChunkName: "prismjs" */ 'prismjs/themes/prism.css'), import(/* webpackChunkName: "prismjs" */ 'prismjs')]).then(() => {
			window.Prism.highlightAll();
		});
	},
	getConfigJson: async function (): Promise<ConfigSettings> {
		if (Object.keys(mx.shell.config).length > 0) {
			return mx.shell.config;
		}
		const config = await mx.loadConfigJson();
		if (typeof config.restHosts.default !== 'string') {
			config.restHosts.default = defaultRestHost;
		}
		const wmConfig = await mx.loadWmConfigJson(config.restHosts.default);
		if (config.mode === 'dev') {
			deepMerge(mx.shell.config, wmConfig, config);
		} else {
			deepMerge(mx.shell.config, config, wmConfig);
		}
		return mx.shell.config;
	},
	loadConfigJson: async function (): Promise<RecursivePartial<ConfigSettings> & { restHosts: { default: string } }> {
		const result = await fetch(`./config.json`);
		if (!result.ok) {
			console.warn(result.statusText);
			return mx.getDefaultConfigSettings();
		}
		return extend({}, mx.getDefaultConfigSettings(), await result.json());
	},
	loadWmConfigJson: async function (restHost: string): Promise<ConfigSettings> {
		const result = await fetch(`${restHost}/api/wmconfig`);
		if (!result.ok) {
			// 503 - cuased due to Maintenance mode or no Lisences error (both errors are handled later)
			// that is why we don't throw error here
			if (result.status === 503) {
				return {} as ConfigSettings;
			}
			console.error(result.statusText);
			throw new Error(result.statusText);
		}
		return await result.json();
	},
	getDefaultConfigSettings: () => ({
		restHosts: {
			default: defaultRestHost,
		},
	}),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as Window & { mx: any }).mx = mx;
